/**
 * 每条埋点都会自动带上的身份字段。
 *
 *   user_id        借款人个人 ID（JWT id）；未登录可为空
 *   user_account   登录账号，即 email
 *   customer_id    组织 / 租户 ID（JWT customerid / session cid / URL）
 *   org_name       组织显示名（companyName）
 *
 * 业务 track() 只传事件自己的差异字段。
 */
export const STATSIG_COMMON_FIELDS = {
    USER_ID: 'user_id',
    USER_ACCOUNT: 'user_account',
    CUSTOMER_ID: 'customer_id',
    ORG_NAME: 'org_name',
} as const

export type StatsigIdentity = {
    user_id?: string
    user_account?: string
    customer_id?: string
    org_name?: string
}

type JwtIdentity = {
    id?: string
    loanofficerid?: string
    customerid?: string
    email?: string
}

export function parseJwtPayload(token?: string | null): JwtIdentity | null {
    if (!token) return null
    try {
        const part = token.split('.')[1]
        if (!part) return null
        let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
        const pad = b64.length % 4
        if (pad) b64 += '===='.slice(0, 4 - pad)
        const parsed = JSON.parse(atob(b64))
        if (!parsed || typeof parsed !== 'object') return null
        return parsed as JwtIdentity
    } catch {
        return null
    }
}

export function readJwtIdentity(): StatsigIdentity {
    if (typeof window === 'undefined') return {}
    const jwt = parseJwtPayload(sessionStorage.getItem('ZeitroA'))
    if (!jwt) return {}
    return {
        user_id: String(jwt.id || jwt.loanofficerid || ''),
        user_account: (jwt.email || '').toLowerCase().trim(),
        customer_id: jwt.customerid || '',
    }
}

/** PreApp 未登录时从 URL / session 补 customer_id。 */
export function readSessionCustomerIdentity(): StatsigIdentity {
    if (typeof window === 'undefined') return {}
    let customer_id = sessionStorage.getItem('cid') || ''
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('customerid')
        if (fromUrl) customer_id = fromUrl
    } catch {
        // ignore
    }
    return {
        customer_id: customer_id || '',
        org_name: sessionStorage.getItem('companyName') || '',
    }
}

function pick(...values: Array<string | undefined | null>): string {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim()
    }
    return ''
}

export function mergeIdentity(
    stored?: StatsigIdentity | null,
    jwt?: StatsigIdentity | null,
    extras?: StatsigIdentity | null
): StatsigIdentity {
    return {
        user_id: pick(extras?.user_id, stored?.user_id, jwt?.user_id),
        user_account: pick(
            extras?.user_account,
            stored?.user_account,
            jwt?.user_account
        ).toLowerCase(),
        customer_id: pick(extras?.customer_id, stored?.customer_id, jwt?.customer_id),
        org_name: pick(extras?.org_name, stored?.org_name),
    }
}
